/**
 * SotaStepper — lightweight informational stepper for the State of the Art workflow.
 *
 * Stages: Scoping → Review → Search → Enrich → Finalize
 * Current stage is derived from the generation_status field of the sub-project.
 * This component is purely informational (non-blocking).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

type GenerationStatus = 'empty' | 'scoping' | 'draft' | 'enriched' | 'finalized';

interface SotaStepperProps {
  generationStatus: GenerationStatus | undefined;
}

const STAGES: { key: string; statuses: GenerationStatus[] }[] = [
  { key: 'sota.stepper.scoping',  statuses: ['scoping'] },
  { key: 'sota.stepper.review',   statuses: ['draft'] },
  { key: 'sota.stepper.search',   statuses: ['draft'] },
  { key: 'sota.stepper.enrich',   statuses: ['enriched'] },
  { key: 'sota.stepper.finalize', statuses: ['finalized'] },
];

function resolveActiveIndex(status: GenerationStatus | undefined): number {
  if (!status || status === 'empty') return -1;
  if (status === 'scoping') return 0;
  if (status === 'draft') return 2;   // "Search" stage (covers review + search)
  if (status === 'enriched') return 3;
  if (status === 'finalized') return 4;
  return -1;
}

const SotaStepper: React.FC<SotaStepperProps> = ({ generationStatus }) => {
  const { t } = useTranslation();
  const activeIdx = resolveActiveIndex(generationStatus);

  if (activeIdx < 0) return null;

  return (
    <div className="sota-stepper flex items-center gap-1 px-3 py-1 text-xs text-gray-500 border-b border-gray-100 bg-gray-50 flex-shrink-0 overflow-x-auto">
      {STAGES.map((stage, idx) => {
        const isDone = idx < activeIdx;
        const isActive = idx === activeIdx;
        return (
          <React.Fragment key={stage.key}>
            {idx > 0 && <span className="text-gray-300 flex-shrink-0">›</span>}
            <span
              className={`flex-shrink-0 whitespace-nowrap ${
                isActive
                  ? 'font-semibold text-blue-600'
                  : isDone
                    ? 'text-gray-400 line-through'
                    : 'text-gray-300'
              }`}
            >
              {t(stage.key)}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SotaStepper;
